import { Env } from "../../enums/env.enum";
import { APPS } from "../constants/constants";
import { App } from "../../interfaces/comps/app.interface";
import { Logo } from "../../enums/logo.enum";
import { Theme } from "../../enums/theme.enum";
import {
    EmptyRoutes,
    TrialCombinedRoutes,
    DefaultCombinedRoutes
} from "../../../components/routes/routers.comp";

const mbaCombinedApp: App = {
    subdomains: [Env.mba, "dev", "prod"],
    routes: DefaultCombinedRoutes,
    lightTheme: Theme.light,
    darkTheme: Theme.dark,
    headerLogo: Logo.mba,
    logo: Logo.mba,
    color: "#FFFFFF",
    adminPanel: true,
    trialMode: false,
    devMode: false,
    main: true
};

const getSubdomain = (location: string) => {
    const locationParts = location.split(".");
    const sliceTill = locationParts.slice(-1)[0] === "localhost" ? -1 : -2;
    return locationParts.slice(0, sliceTill).join(".");
};

const getCurrentApp = () => {
    return mbaCombinedApp;
};

export const getApp = (env: Env) => {
    const currentSubdomain = getSubdomain(window.location.hostname);
    const environment = currentSubdomain.includes("admin") ? `admin.${env}` : env;

    return APPS.find(app => app.subdomains.find(subdomain => subdomain === environment) != null);
};

export const getRoutes = (isTrial: boolean | undefined) => {
    return isTrial == undefined
        ? EmptyRoutes
        : isTrial
          ? TrialCombinedRoutes
          : DefaultCombinedRoutes;
};

export const getEmptyRoutes = () => {
    return EmptyRoutes;
};

export const getAppRoutes = () => {
    return getCurrentApp().routes;
};

export const getHeaderLogo = () => {
    return getCurrentApp().headerLogo;
};

export const getAppLogo = () => {
    return getCurrentApp().logo;
};

export const getAppColor = () => {
    return getCurrentApp().color;
};

export const lightTheme = getCurrentApp().lightTheme;
export const darkTheme = getCurrentApp().darkTheme;
export const isAdminPanel = getCurrentApp().adminPanel;
export const isTrialMode = getCurrentApp().trialMode;
export const isDevMode = getCurrentApp().devMode;

/**
 * Checks if the current subdomain is the login domain
 * @returns True if running on the configured login domain
 */
export const isAuthDomain = (): boolean => {
    const loginDomain = process.env.REACT_APP_LOGIN_DOMAIN;
    if (!loginDomain) {
        // Fallback if env var not set
        return false;
    }

    try {
        const loginUrl = new URL(loginDomain);
        const loginSubdomain = getSubdomain(loginUrl.hostname);
        const currentSubdomain = getSubdomain(window.location.hostname);
        return currentSubdomain === loginSubdomain;
    } catch (e) {
        return false;
    }
};

/**
 * Extracts the main domain from the current hostname
 * @returns The main domain (e.g., "kenbakke.com" from "trial.kenbakke.com")
 */
export const getMainDomain = (): string => {
    const parts = window.location.hostname.split(".");

    // Handle localhost
    if (parts[parts.length - 1] === "localhost") {
        return "localhost";
    }

    // Return last two parts (domain.com)
    return parts.slice(-2).join(".");
};

/**
 * Constructs a full login domain URL for the given path
 * @param path - The path on the login domain (e.g., "/login?returnUrl=...")
 * @returns Full login domain URL
 */
export const constructAuthDomainUrl = (path: string): string => {
    const hostname = window.location.hostname;

    // Handle localhost development
    if (hostname === "localhost" || hostname === "127.0.0.1") {
        return `http://localhost:${window.location.port || 3000}${path}`;
    }

    const loginDomain = process.env.REACT_APP_LOGIN_DOMAIN;
    if (!loginDomain) {
        throw new Error("REACT_APP_LOGIN_DOMAIN environment variable not set");
    }

    // Construct full URL by combining login domain with path
    const url = new URL(path, loginDomain);
    return url.toString();
};
